import { styled } from "goober";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  isSqliteMediaType,
  mediaTypeFromFileName,
} from "../../../shared/attachmentPolicy";
import { queries } from "../../../shared/queries";
import { useQuery } from "../../hooks/useQuery";
import {
  personalVolumePath,
  spaceVolumePath,
  volumeFilePath,
} from "../../lib/apiPaths";
import { apiUrl } from "../../lib/tenantOrigin";
import { FilePreviewModal } from "../AttachmentHoverPreview";
import { Button } from "../Button";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import { Modal } from "../Modal";
import { Text } from "../Text";
import { BranchesColumn } from "./columns/BranchesColumn";
import { DirectoryColumn } from "./columns/DirectoryColumn";
import { FileInfoColumn } from "./columns/FileInfoColumn";
import { PersonalSectionColumn } from "./columns/PersonalSectionColumn";
import { PersonalVolumesColumn } from "./columns/PersonalVolumesColumn";
import { VolumesColumn } from "./columns/VolumesColumn";
import type { ColumnLevel, PathSegment } from "./types";
import { useFileBrowserNav } from "./useFileBrowserNav";

type Props = {
  onClose: () => void;
  spaceId: string;
  branchId?: string;
  workflowId?: string;
  volume?: string;
  section?: string;
};

const Content = styled("div")`
  width: 80vw;
  max-width: 1200px;
  height: 80vh;
  max-height: 800px;
  display: flex;
  flex-direction: column;
`;

const Header = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
`;

const HeaderIcon = styled("div")`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--alpha4);
  border-radius: 8px;
`;

const Body = styled("div")`
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  border-top: 0.5px solid var(--border-alpha);
`;

export function FileBrowser({
  onClose,
  spaceId,
  branchId,
  workflowId,
  volume,
  section,
}: Props) {
  const [personalSpace] = useQuery(queries.personalSpace());
  const [volumes] = useQuery(queries.spaceVolumes({ spaceId }));
  const branch =
    branchId || workflowId
      ? volumes.find((v) =>
          branchId
            ? v.branchId === branchId
            : v.branch?.workflowId === workflowId && v.branch?.name === "main"
        )?.branch
      : undefined;

  const initialPath: PathSegment[] = [];
  if (personalSpace?.id === spaceId && (section || branch)) {
    initialPath.push(
      section === "chats"
        ? { level: "personal-section", id: "chats", label: "Chats" }
        : { level: "personal-section", id: "workflows", label: "Workflows" }
    );
  }
  if (branch && section !== "chats") {
    initialPath.push({
      level: "branches",
      id: branch.id,
      label:
        branch.name === "main"
          ? (branch.workflow?.name ?? branch.name)
          : branch.name,
    });
    if (volume) {
      initialPath.push({ level: "volumes", id: volume, label: volume });
    }
  }

  return (
    <FileBrowserInner
      key={[spaceId, ...initialPath.map((s) => `${s.level}:${s.id}`)].join("|")}
      spaceId={spaceId}
      onClose={onClose}
      initialPath={initialPath}
    />
  );
}

type PreviewFile = {
  pathBase: string;
  relativePath: string;
  fileName: string;
  sizeBytes: number;
};

function volumePathBase(path: PathSegment[], spaceId: string): string | null {
  const personalVolumeSeg = path.find((s) => s.level === "personal-volumes");
  if (personalVolumeSeg) {
    return personalVolumePath(personalVolumeSeg.id);
  }
  const branchSeg = path.find((s) => s.level === "branches");
  const volumeSeg = path.find((s) => s.level === "volumes");
  if (!branchSeg || !volumeSeg) return null;
  return spaceVolumePath(spaceId, branchSeg.id, volumeSeg.id);
}

function previewFileFromPath(
  path: PathSegment[],
  spaceId: string
): PreviewFile | null {
  const tail = path[path.length - 1];
  if (!tail || tail.meta?.kind !== "file") return null;
  const base = volumePathBase(path, spaceId);
  if (!base) return null;
  return {
    pathBase: base,
    relativePath: tail.meta?.relativePath ?? tail.id,
    fileName: tail.label,
    sizeBytes: Number(tail.meta?.size ?? 0),
  };
}

function previewUrl(file: PreviewFile, externalApiUrl: string): string {
  return `${externalApiUrl}${volumeFilePath(file.pathBase, file.relativePath)}`;
}

function downloadUrl(file: PreviewFile, externalApiUrl: string): string {
  const url = new URL(previewUrl(file, externalApiUrl), location.href);
  url.searchParams.set("download", "1");
  if (isSqliteMediaType(mediaTypeFromFileName(file.fileName))) {
    url.searchParams.set("sqlite", "1");
  }
  return url.href;
}

function FileBrowserInner({
  spaceId,
  onClose,
  initialPath,
}: {
  spaceId: string;
  onClose: () => void;
  initialPath: PathSegment[];
}) {
  const externalApiUrl = apiUrl();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [personalSpace] = useQuery(queries.personalSpace());
  const [spaceVolumes, spaceVolumesResult] = useQuery(
    queries.spaceVolumes({ spaceId })
  );
  const pathRef = useRef<PathSegment[]>(initialPath);

  const togglePreview = useCallback(() => {
    setPreviewOpen((open) => {
      if (open) return false;
      return previewFileFromPath(pathRef.current, spaceId) !== null;
    });
  }, [spaceId]);

  const {
    path,
    focusedColumnIndex,
    focusedRowIndex,
    scrollRef,
    selectItem,
    replaceItem,
    registerColumnItemCount,
  } = useFileBrowserNav(initialPath, onClose, !previewOpen, togglePreview);

  // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
  pathRef.current = path;
  const currentFile = previewFileFromPath(path, spaceId);
  const hasCurrentFile = currentFile !== null;
  const currentFileUrl = currentFile
    ? previewUrl(currentFile, externalApiUrl)
    : null;

  useEffect(() => {
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    if (previewOpen && !hasCurrentFile) setPreviewOpen(false);
  }, [hasCurrentFile, previewOpen]);

  const makeOnSelect = useCallback(
    (columnIndex: number, level: ColumnLevel) =>
      (
        id: string,
        label: string,
        rowIndex: number,
        meta?: Record<string, string>
      ) => {
        selectItem(columnIndex, level, id, label, rowIndex, meta);
      },
    [selectItem]
  );

  const makeOnReplace = useCallback(
    (columnIndex: number, level: ColumnLevel) =>
      (id: string, label: string, meta?: Record<string, string>) => {
        replaceItem(columnIndex, level, id, label, meta);
      },
    [replaceItem]
  );

  const columnProps = (columnIndex: number) => ({
    focusedColumn: focusedColumnIndex === columnIndex,
    focusedIndex: focusedColumnIndex === columnIndex ? focusedRowIndex : -1,
    columnIndex,
    onRegisterCount: registerColumnItemCount,
  });

  const seg = (level: ColumnLevel) => path.find((s) => s.level === level);

  const pushDirectoryTail = (
    columns: React.ReactNode[],
    pathBase: string,
    volumeName: string,
    volumeKey: string,
    rootColumnIndex: number
  ) => {
    const dirSegments = path.filter((s) => s.level === "directory");

    columns.push(
      <DirectoryColumn
        key={`dir-root-${volumeKey}`}
        pathBase={pathBase}
        relativePath=""
        heading={volumeName}
        selectedId={dirSegments[0]?.id ?? null}
        onSelect={makeOnSelect(rootColumnIndex, "directory")}
        onReplace={makeOnReplace(rootColumnIndex, "directory")}
        {...columnProps(rootColumnIndex)}
      />
    );

    for (let i = 0; i < dirSegments.length; i++) {
      const segEntry = dirSegments[i]!;
      const colIndex = rootColumnIndex + 1 + i;
      const kind = segEntry.meta?.kind;
      const relativePath = segEntry.meta?.relativePath ?? segEntry.id;

      if (kind === "file") {
        columns.push(
          <FileInfoColumn
            key={`info-${volumeKey}-${relativePath}`}
            fileName={segEntry.label}
            fullPath={`/storage/${volumeName}/${relativePath}`}
            size={Number(segEntry.meta?.size ?? 0)}
            modifiedAt={Number(segEntry.meta?.modifiedAt ?? 0)}
            columnIndex={colIndex}
            onRegisterCount={registerColumnItemCount}
            onPreview={() => setPreviewOpen(true)}
            onDownload={() => {
              if (!currentFile) return;
              const a = document.createElement("a");
              a.href = downloadUrl(currentFile, externalApiUrl);
              a.download = segEntry.label;
              a.rel = "noopener noreferrer";
              a.click();
            }}
          />
        );
        break;
      }

      const childSeg = dirSegments[i + 1];
      columns.push(
        <DirectoryColumn
          key={`dir-${volumeKey}-${relativePath}`}
          pathBase={pathBase}
          relativePath={relativePath}
          heading={segEntry.label}
          selectedId={childSeg?.id ?? null}
          onSelect={makeOnSelect(colIndex, "directory")}
          onReplace={makeOnReplace(colIndex, "directory")}
          {...columnProps(colIndex)}
        />
      );
    }
  };

  const renderColumns = () => {
    const columns: React.ReactNode[] = [];

    if (!personalSpace) return columns;

    if (spaceId === personalSpace.id) {
      columns.push(
        <PersonalSectionColumn
          key="personal-section"
          selectedId={seg("personal-section")?.id ?? null}
          onSelect={makeOnSelect(0, "personal-section")}
          onReplace={makeOnReplace(0, "personal-section")}
          {...columnProps(0)}
        />
      );

      const sectionSeg = seg("personal-section");
      if (!sectionSeg) return columns;

      if (sectionSeg.id === "chats") {
        columns.push(
          <PersonalVolumesColumn
            key="personal-volumes"
            selectedId={seg("personal-volumes")?.id ?? null}
            onSelect={makeOnSelect(1, "personal-volumes")}
            onReplace={makeOnReplace(1, "personal-volumes")}
            {...columnProps(1)}
          />
        );

        const pvSeg = seg("personal-volumes");
        if (!pvSeg) return columns;
        const volumeName = pvSeg.id;
        pushDirectoryTail(
          columns,
          personalVolumePath(volumeName),
          volumeName,
          `pv-${pvSeg.id}`,
          2
        );
        return columns.slice(0, focusedColumnIndex + 2);
      }

      columns.push(
        <BranchesColumn
          key={`branches-${spaceId}`}
          spaceId={spaceId}
          selectedId={seg("branches")?.id ?? null}
          onSelect={makeOnSelect(1, "branches")}
          onReplace={makeOnReplace(1, "branches")}
          {...columnProps(1)}
        />
      );

      const branchSeg = seg("branches");
      if (!branchSeg) return columns;

      columns.push(
        <VolumesColumn
          key={`volumes-${spaceId}-${branchSeg.id}`}
          spaceId={spaceId}
          branchId={branchSeg.id}
          selectedId={seg("volumes")?.id ?? null}
          onSelect={makeOnSelect(2, "volumes")}
          onReplace={makeOnReplace(2, "volumes")}
          {...columnProps(2)}
        />
      );

      const volumeSeg = seg("volumes");
      if (!volumeSeg) return columns;
      const volumeName = volumeSeg.id;
      pushDirectoryTail(
        columns,
        spaceVolumePath(spaceId, branchSeg.id, volumeName),
        volumeName,
        `v-${volumeSeg.id}`,
        3
      );
      return columns.slice(0, focusedColumnIndex + 2);
    }

    columns.push(
      <BranchesColumn
        key={`branches-${spaceId}`}
        spaceId={spaceId}
        selectedId={seg("branches")?.id ?? null}
        onSelect={makeOnSelect(0, "branches")}
        onReplace={makeOnReplace(0, "branches")}
        {...columnProps(0)}
      />
    );

    const branchSeg = seg("branches");
    if (!branchSeg) return columns;

    columns.push(
      <VolumesColumn
        key={`volumes-${spaceId}-${branchSeg.id}`}
        spaceId={spaceId}
        branchId={branchSeg.id}
        selectedId={seg("volumes")?.id ?? null}
        onSelect={makeOnSelect(1, "volumes")}
        onReplace={makeOnReplace(1, "volumes")}
        {...columnProps(1)}
      />
    );

    const volumeSeg = seg("volumes");
    if (!volumeSeg) return columns;
    const volumeName = volumeSeg.id;
    pushDirectoryTail(
      columns,
      spaceVolumePath(spaceId, branchSeg.id, volumeName),
      volumeName,
      `v-${volumeSeg.id}`,
      2
    );
    return columns.slice(0, focusedColumnIndex + 2);
  };

  const showEmptyState =
    !!personalSpace &&
    personalSpace.id !== spaceId &&
    spaceVolumesResult.type === "complete" &&
    spaceVolumes.length === 0;

  return (
    <Modal open onClose={previewOpen ? () => {} : onClose}>
      <Content>
        <Header>
          <HeaderIcon>
            <Icon name="box" size={16} color="var(--text-primary)" />
          </HeaderIcon>
          <Text variant="text-regular-md">Storage</Text>
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Button size="l" variant="secondary" icon="x" onClick={onClose} />
          </div>
        </Header>
        <Body ref={scrollRef}>
          {showEmptyState ? (
            <EmptyState
              iconName="box"
              title="No stored files yet"
              description="Files your workflows read and write will appear here."
            />
          ) : (
            renderColumns()
          )}
        </Body>
      </Content>
      {currentFile && currentFileUrl && (
        <FilePreviewModal
          key={currentFileUrl}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          fileName={currentFile.fileName}
          mediaType={mediaTypeFromFileName(currentFile.fileName)}
          sizeBytes={currentFile.sizeBytes}
          url={currentFileUrl}
        />
      )}
    </Modal>
  );
}
